import cx from 'classnames';
import { useEffect, useState } from 'react';
import styles from './RegisterGuestUserPopup.module.scss';
import InfoDialogUI from '../components/InfoDialog/InfoDialogUI';
import { icons } from '../assets';
import { ButtonV2 } from '../components/ButtonV2/ButtonV2';
import SignupImage from '../assets/images/SignupImage.webp';
import {
  EMAIL_VERIFIED,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
} from '../constants/projectConstants';
const { GoogleNewIcon, CloseIcon, VerifyEmailIcon } = icons;
import { emailIsValid, getLocalStorage, googleTagEventPush, setLocalStorage, textLowerCaseAndTrim, wait } from '../helpers/jsHelper';
import {
  PRICING_ROUTE,
  PRIVACY_POLICY_ROUTE,
  TERMS_OF_USE_ROUTE,
} from '../constants/routePathConstants';
import { Link, useNavigate } from 'react-router-dom';
import { apiConvertGuestUser } from '../helpers/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSegmentData,
  selectShowVerifyEmailPopup,
  selectSignupPopOpen,
  selectSignupPopOpenedFrom,
  setShowVerifyEmailPopup,
  setSignupPopOpen,
} from '../redux/slicers/uiSlicer';
import { useUserContext } from '../contexts/UserContext';
import { loginDataAfterToken } from './GuestRegisterWithToken';
import { errorToast, successToast } from '../helpers/toastHelper';
import { useGoogleLogin } from '@react-oauth/google';
import {
  selectGuestSignupComplete,
  setGuestSignupComplete,
} from '../redux/slicers/admin/curatorSlicer';
import { useVerifyEmail } from '../common/VerifyYourEmail';
import { LogConsole } from '../helpers/logEvents';
import { callTrackEvent } from '../helpers/segmentHelper';
import { userPilotEvents } from '../common/Userpilot/userpilotHelper';
import { GOOGLE_SIGN_UP } from '../config/constant/projectConstant';


export default function RegisterGuestUserPopup() {
  return (
    <>
      <PopupUI />
      <VerifyYourEmailPopup />
    </>
  );
}

export const PopupUI = () => {
  const show = useSelector(selectSignupPopOpen);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [hidePassword, setHidePassword] = useState(true);
  const [password, setPassword] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const userCtx = useUserContext();
  const navigate = useNavigate();
  const allSegmentData = useSelector(selectSegmentData);
  const { trackSignUpPopupOpened } = userPilotEvents();

  useEffect(() => {
    if (show) trackSignUpPopupOpened();
  }, [show]);

  const onClose = () => {
    setLoader(false);
    setName('');
    setEmail('');
    setPassword('');
    setHidePassword(true);
    setIsEmailValid(true);
    dispatch(setSignupPopOpen(false));
  };

  const onFailure = (res) => {
    console.error(res);
    errorToast(res?.error_description);
    setLoader(false);
  };

  const onSuccess = (res) => {
    LogConsole(res, 'res google login');
    onSignup(res?.access_token);
  };

  const loginClick = useGoogleLogin({
    onSuccess: onSuccess,
    onError: onFailure,
  });

  const callSegmentEvent = (name, email) => {
    const dataForSegment = {
      User_name: name,
      User_email: email,
      camera_angle_id: allSegmentData?.Camera_id || '',
      camera_angle_name: allSegmentData?.Camera_name || '',
      FileName: allSegmentData?.FileName || '',
      is_preview: allSegmentData?.is_preview || false,
      Quality: allSegmentData?.Quality || '1080p',
    };
    dataForSegment['Resolution(W)'] = allSegmentData?.resoX || '';
    dataForSegment['Resolution(H)'] = allSegmentData?.resoY || '';
    callTrackEvent('Sign up now', dataForSegment, true);
  };

  const onSignup = async (accessToken) => {
    if ((name && email && password && isEmailValid) || accessToken) {
      setLoader(true);

      const firstName = name.split(' ')[0];
      const lastName = name.split(' ')[1];

      const formObj = new FormData();
      if (accessToken) {
        formObj.append('access_token', accessToken);
        setLocalStorage(GOOGLE_SIGN_UP, true)
      } else {
        formObj.append('email', textLowerCaseAndTrim(email));
        formObj.append('first_name', firstName);
        formObj.append('last_name', lastName || '');
        formObj.append('password', password);
      }
      for (let pair of formObj.entries()) {
        LogConsole(`${pair[0]}: ${pair[1]}`, 'formdata check');
      }
      const response = await apiConvertGuestUser(formObj);
      LogConsole('response check response', response);
      if (!response || (!response?.status && !response.access)) {
        setLoader(false);
        return;
      }
      googleTagEventPush({
        url: window.location.href,
        event_name: 'conversion',
        send_to: 'AW-10868671574/PRyJCM2UpYEaENaIy74o', // Signup conversion event
      });
      const { access, is_active_org, is_email_verified, refresh, is_guest, id } = response;
      const parsedData = {
        access,
        is_active_org,
        is_email_verified,
        refresh,
        is_guest,
        openImages: false,
        id,
      };

      LogConsole(parsedData, 'saving data');
      loginDataAfterToken({
        parsedData,
        userCtx,
        navigate,
        triggerEvent: true,
        isGoogleSignup: accessToken ? true : false,
      });
      await wait(1000);
      dispatch(setGuestSignupComplete(true));
      successToast('Signup successful');
      setLoader(false);
      callSegmentEvent(name, email);
      await wait(2000);
      dispatch(setShowVerifyEmailPopup(true));
    }
  };

  const changeName = (e) => {
    setName(e.target.value);
  };

  const changePassword = (e) => {
    setPassword(e.target.value);
  };

  const changeEmail = (e) => {
    setEmail(e.target.value);
    if (email) {
      const isYourEmailValid = emailIsValid(e.target.value);
      if (isYourEmailValid) {
        setIsEmailValid(isYourEmailValid);
      }
    }
  };

  const emailChangeBlur = (e) => {
    const isYourEmailValid = emailIsValid(e.target.value);
    setIsEmailValid(isYourEmailValid);
  };

  const checkForDisabled = () => {
    return !(name && email && password && isEmailValid);
  };

  return (
    <InfoDialogUI
      hideTitle
      dialogClassName={styles.dialog}
      bodyClassName={styles.modalBody}
      onClose={onClose}
      show={show}
      backdropClassName={styles.backdrop}
      theme="v2">
      <div className={styles.container}>
        <div className={styles.loginSide}>
          <div className={styles.welcome}>
            <div className={styles.welcomeN}>Welcome to imagine.io</div>
            <div className={styles.useEmail}>
              Use your email or another service to continue with imagine.io (its free)!
            </div>
          </div>
          <div className={styles.continueGoogle} onClick={loginClick}>
            <div className={styles.contentGoogle}>
              <GoogleNewIcon />
              <div className={styles.continueGoogle2}>Continue with Google</div>
            </div>
          </div>
          <div className={styles.orContainer}>
            <div className={styles.orContent}>OR</div>
          </div>
          <div className={styles.inputBoxes}>
            <div className={styles.inputContainer}>
              <div className={styles.titleText}>Full name</div>
              <input
                type="text"
                className={styles.inputField}
                placeholder="Enter name"
                maxLength={50}
                value={name}
                onChange={changeName}
              />
            </div>
            <div className={styles.inputContainer}>
              <div className={styles.titleText}>Email Address</div>
              <input
                type="text"
                className={styles.inputField}
                placeholder="Enter email"
                value={email}
                onChange={changeEmail}
                onBlur={(e) => emailChangeBlur(e)}
              />
              {!isEmailValid && <div style={{ color: 'red' }}>Invalid email</div>}
            </div>
            <div className={styles.inputContainer}>
              <div className={styles.titleText}>Password</div>
              <div className={styles.passwordContainer}>
                <input
                  type={hidePassword ? 'password' : 'text'}
                  className={styles.inputField}
                  placeholder="Enter password"
                  maxLength={MAX_PASSWORD_LENGTH}
                  minLength={MIN_PASSWORD_LENGTH}
                  onChange={changePassword}
                />
                <div className={styles.showText} onClick={() => setHidePassword(!hidePassword)}>
                  {hidePassword ? 'Show' : 'Hide'}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.signupBtnContainer}>
            <ButtonV2
              color="primary"
              className={`${checkForDisabled() ? styles.disableClass : ''} ${styles.btnWork}`}
              fullWidth={true}
              loading={loader}
              onClick={() => onSignup()}>
              Sign up
            </ButtonV2>
          </div>
          <div className={styles.tandcText}>
            By continuing, you agree to our{' '}
            <Link to={TERMS_OF_USE_ROUTE} target="_blank" rel="noopener noreferrer">
              <span className={styles.highlightText}>Terms of Services</span>
            </Link>{' '}
            and{' '}
            <Link to={PRIVACY_POLICY_ROUTE} target="_blank" rel="noopener noreferrer">
              <span className={styles.highlightText}>Privacy Policy</span>
            </Link>
          </div>
        </div>
        <div className={styles.imageSide}>
          <div className={styles.closeIcon} onClick={onClose}>
            <CloseIcon color="#fff" />
          </div>
          <img loading="lazy" src={SignupImage} className={styles.imageSide} />
        </div>
      </div>
    </InfoDialogUI>
  );
};

export const VerifyYourEmailPopup = () => {
  const dispatch = useDispatch();
  const { handleResend } = useVerifyEmail();
  const isGuestSignupComplete = useSelector(selectGuestSignupComplete);
  const show = useSelector(selectShowVerifyEmailPopup);

  const onClose = () => dispatch(setShowVerifyEmailPopup(false));

  if (getLocalStorage(EMAIL_VERIFIED, true) || !isGuestSignupComplete || !show) return null;

  return (
    <InfoDialogUI
      hideTitle
      dialogClassName={styles.dialogV2}
      bodyClassName={styles.modalBody}
      onClose={onClose}
      show={show}
      backdropClassName={styles.backdrop}
      theme="v2">
      <div className={styles.wrapper}>
        <div className={cx(styles.closeIcon, styles.closeIconV2)} onClick={onClose}>
          <CloseIcon color="#fff" />
        </div>
        <div className={styles.containerV2}>
          <p className={styles.text1}>We&#39;ve sent you a confirmation email</p>
          <div className={styles.emailIcon}>
            <VerifyEmailIcon />
          </div>
          <p className={styles.text2}>Verify to get 5 Free images !</p>
          <p className={styles.text3}>
            <span>Check the link in your email to confirm you account</span>
            <span>If you can&#39;t find the email check the spam or promotional folder</span>
          </p>
          <p className={styles.text4}>
            Didn&#39;t receive an email?&nbsp;&nbsp;
            <a className="cursor-pointer" onClick={handleResend}>
              Resend
            </a>
          </p>
        </div>
      </div>
    </InfoDialogUI>
  );
};
